import React from "react";
import AddHardwareFormView from "./AddHardwareFormView/AddHardwareFormView";
import { HardwarePatch, HardwaresType } from "../../types/hardware.types";
import useHardware from "../../hooks/useHardware";
import inputValidityChecker from "../../../../utils/inputValidityChecker";
import { extractAxoisErrorsMessage } from "../../../errorHandler/utils/errors.utils";

export interface NewHardwareData {
  deviceID?: string;
  hardwareType?: HardwaresType;
  secretKey?: string;
  secretKeyCredit?: string;
}

export interface NewHardwareErrorState {
  isErrorDeviceID: boolean;
  isErrorHardwareType: boolean;
  isErrorSecretKey: boolean;
  isErrorSecretKeyCredit?: boolean;
  allgood: boolean;
  errorMessage: string | null | undefined;
}

const INITIAL_ERROR_STATE: NewHardwareErrorState = {
  isErrorDeviceID: false,
  isErrorHardwareType: false,
  isErrorSecretKey: false,
  isErrorSecretKeyCredit: false,
  allgood: false,
  errorMessage: "",
};

const INITIAL_FORM_DATA: NewHardwareData = {
  deviceID: undefined,
};

export const checkValidityHardware = (
  data: HardwarePatch
): NewHardwareErrorState => {
  const messagesList = [];
  const errorState: NewHardwareErrorState = {
    isErrorDeviceID: false,
    isErrorHardwareType: false,
    isErrorSecretKey: false,
    allgood: true,
    errorMessage: "",
  };

  if (!inputValidityChecker.validateNotEmptyText(data.hardware_type)) {
    errorState.isErrorHardwareType = true;
    errorState.allgood = false;
    messagesList.push("Hardware Type est obligatoire");
  }

  if (!inputValidityChecker.validateNotEmptyText(data.hardware_device_id)) {
    errorState.isErrorDeviceID = true;
    errorState.allgood = false;
    messagesList.push("Device ID est obligatoire");
  } else if (
    data.hardware_type === "STATION" &&
    !inputValidityChecker.validateNumberInt(data.hardware_device_id)
  ) {
    errorState.isErrorDeviceID = true;
    errorState.allgood = false;
    messagesList.push("Device ID d'une station doit être un nombre");
  }

  if (data.hardware_type === "STATION" && !inputValidityChecker.validateNotEmptyText(data.hardware_secret_key)) {
    errorState.isErrorSecretKey = true;
    errorState.allgood = false;
    messagesList.push("Secret Key est obligatoire");
  }

  if ( data.hardware_type === "STATION" && !inputValidityChecker.validateNotEmptyText(data.hardware_secret_key_credit)) {
    errorState.isErrorSecretKeyCredit = true;
    errorState.allgood = false;
    messagesList.push("Clé secrète des avoirs est obligatoire");
  }

  if (messagesList.length > 0) {
    errorState.errorMessage = messagesList.join(", ");
  } else {
    errorState.allgood = true;
  }

  return errorState;
};

export default function AddNewHardwareFromPresenter({
  onSuccess,
}: {
  onSuccess?: () => void;
}) {
  const [formData, setFormData] = React.useState<NewHardwareData>({
    ...INITIAL_FORM_DATA,
  });
  const [errorStatus, setErrorStatus] =
    React.useState<NewHardwareErrorState>(INITIAL_ERROR_STATE);
  const { createNew } = useHardware();

  const convertFormDataToPatch = (formData: NewHardwareData): HardwarePatch => {
    const deviceIdConverted: string | undefined = formData.deviceID;
    return {
      hardware_device_id: deviceIdConverted,
      hardware_type: formData.hardwareType,
      hardware_secret_key: formData.secretKey,
      hardware_secret_key_credit: formData.secretKeyCredit,
    };
  };

  const setFormDataValue = (key: keyof NewHardwareData, value: string) => {
    setFormData({
      ...formData,
      [key]: value,
    });
  };

  const handleSubmit = () => {
    const hardwarePatch = convertFormDataToPatch(formData);
    const errorState = checkValidityHardware(hardwarePatch);
    if (errorState.allgood) {
      createNew.mutate({
        newData: hardwarePatch,
        callback: ({ result, error }) => {
          if (error) {
            const message = extractAxoisErrorsMessage(error);
            setErrorStatus({ ...errorState, errorMessage: message });
          }
          if (result) {
            setFormData({ ...INITIAL_FORM_DATA });
            setErrorStatus({ ...INITIAL_ERROR_STATE });
            if (onSuccess) {
              onSuccess();
            }
          }
        },
      });
    } else {
      setErrorStatus(errorState);
    }
  };

  return (
    <AddHardwareFormView
      setFormData={setFormDataValue}
      errorStatus={errorStatus}
      formData={formData}
      handleSubmit={handleSubmit}
      isLoading={createNew.isLoading}
      messageError={errorStatus.errorMessage}
    />
  );
}
